import { Injectable } from '@angular/core';

export interface AppColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
}

export interface AppColors {
  light: AppColorPalette;
  dark: AppColorPalette;
}

@Injectable({ providedIn: 'root' })
export class AppColorsService {
  private readonly storageKey = 'AppColors';
  private readonly cssVarKeys = [
    '--app-primary',
    '--app-secondary',
    '--app-accent',
    '--app-background',
    '--app-surface',
    '--app-text',
    '--mat-sys-primary',
    '--mat-sys-on-primary',
    '--mat-sys-secondary',
    '--mat-sys-tertiary',
    '--mat-sys-background',
    '--mat-sys-on-background',
    '--mat-sys-surface',
    '--mat-sys-on-surface',
    '--mat-app-background-color',
    '--mat-sidenav-content-background-color',
    '--mat-sys-surface-container-low',
    '--mat-sys-surface-container',
    '--mat-sys-surface-container-high',
    '--mat-card-outlined-container-color',
    '--mat-card-elevated-container-color',
    // MDC button component tokens — not derived from --mat-sys-primary at runtime
    '--mdc-filled-button-container-color',
    '--mdc-filled-button-label-text-color',
    '--mdc-protected-button-container-color',
    '--mdc-protected-button-label-text-color',
    '--mat-text-button-label-text-color',
    '--mat-text-button-state-layer-color',
    '--mdc-outlined-button-label-text-color',
    '--mdc-outlined-button-outline-color',
    '--mat-icon-button-state-layer-color',
    '--mat-fab-container-color',
    '--mat-fab-foreground-color',
  ] as const;

  applyFromLoginResponse(loginResponse: any, theme: string | null = null): string | null {
    var appColors = this.extractAppColors(loginResponse);


/* const appColors = {
            "light": {
                "primary": "#d22819",
                "secondary": "#4c9d00",
                "accent": "#37ff009c",
                "background": "#FFFFFF",
                "surface": "#FFFFFF",
                "text": "#000000"
            },
            "dark": {
                "primary": "#90CAF9",
                "secondary": "#BDBDBD",
                "accent": "#82B1FF",
                "background": "#121212",
                "surface": "#1E1E1E",
                "text": "#FFFFFF"
            }
            };
 */

    if (!appColors) {
      this.clearStoredThemeColors();
    appColors = {
            "light": {
                "primary": "#1976d2",
                "secondary": "#424242",
                "accent": "#82b1ff",
                "background": "#f3f5f8",
                "surface": "#FFFFFF",
                "text": "#17202a"
            },
            "dark": {
                "primary": "#90CAF9",
                "secondary": "#BDBDBD",
                "accent": "#82B1FF",
                "background": "#313131",
                "surface": "#1E1E1E",
                "text": "#FFFFFF"
            }
        };
      //return null;
    }

    localStorage.setItem(this.storageKey, JSON.stringify(appColors));
    this.applyStoredThemeColors(theme);

    const palette = this.getThemePalette(appColors, theme);
    return palette?.primary ?? null;
  }

  applyStoredThemeColors(theme: string | null = null): void {
    const appColors = this.getStoredAppColors();
    if (!appColors) {
      this.clearAppliedThemeColors();
      return;
    }

    const palette = this.getThemePalette(appColors, theme);
    if (!palette) {
      return;
    }

    this.applyPaletteToStyle(document.documentElement.style, palette);
    this.applyPaletteToStyle(document.body.style, palette);
  }

  parseAppColors(rawAppColors: unknown): AppColors | null {
    if (!rawAppColors) {
      return null;
    }

    if (typeof rawAppColors === 'string') {
      try {
        return this.normalizeAppColors(JSON.parse(rawAppColors));
      } catch {
        return null;
      }
    }

    if (typeof rawAppColors === 'object') {
      return this.normalizeAppColors(rawAppColors);
    }

    return null;
  }

  getDefaultAppColors(): AppColors {
    return {
      light: {
        primary: '#1976D2',
        secondary: '#424242',
        accent: '#82B1FF',
        background: '#FFFFFF',
        surface: '#FFFFFF',
        text: '#000000',
      },
      dark: {
        primary: '#90CAF9',
        secondary: '#BDBDBD',
        accent: '#82B1FF',
        background: '#121212',
        surface: '#1E1E1E',
        text: '#FFFFFF',
      },
    };
  }

  saveAndApplyAppColors(appColors: AppColors, theme: string | null = null): void {
    localStorage.setItem(this.storageKey, JSON.stringify(appColors));
    this.applyStoredThemeColors(theme);
  }

  clearStoredThemeColors(): void {
    localStorage.removeItem(this.storageKey);
    this.clearAppliedThemeColors();
  }

  private clearAppliedThemeColors(): void {
    this.clearPaletteFromStyle(document.documentElement.style);
    this.clearPaletteFromStyle(document.body.style);
  }

  private clearPaletteFromStyle(style: CSSStyleDeclaration): void {
    for (const variableName of this.cssVarKeys) {
      style.removeProperty(variableName);
    }
  }

  private applyPaletteToStyle(style: CSSStyleDeclaration, palette: AppColorPalette): void {
    const onPrimary = this.contrastColor(palette.primary);

    style.setProperty('--app-primary', palette.primary);
    style.setProperty('--app-secondary', palette.secondary);
    style.setProperty('--app-accent', palette.accent);
    style.setProperty('--app-background', palette.background);
    style.setProperty('--app-surface', palette.surface);
    style.setProperty('--app-text', palette.text);

    style.setProperty('--mat-sys-primary', palette.primary);
    style.setProperty('--mat-sys-on-primary', onPrimary);
    style.setProperty('--mat-sys-secondary', palette.secondary);
    style.setProperty('--mat-sys-tertiary', palette.accent);
    style.setProperty('--mat-sys-background', palette.background);
    style.setProperty('--mat-sys-on-background', palette.text);
    style.setProperty('--mat-sys-surface', palette.surface);
    style.setProperty('--mat-sys-on-surface', palette.text);

    style.setProperty('--mat-app-background-color', palette.background);
    style.setProperty('--mat-sidenav-content-background-color', palette.background);
    style.setProperty('--mat-sys-surface-container-low', palette.surface);
    style.setProperty('--mat-sys-surface-container', palette.surface);
    style.setProperty('--mat-sys-surface-container-high', palette.surface);
    style.setProperty('--mat-card-outlined-container-color', palette.surface);
    style.setProperty('--mat-card-elevated-container-color', palette.surface);

    // Override MDC component-level button tokens which are baked in at build time
    // and are not re-derived from --mat-sys-primary at runtime.
    style.setProperty('--mdc-filled-button-container-color', palette.primary);
    style.setProperty('--mdc-filled-button-label-text-color', onPrimary);
    style.setProperty('--mdc-protected-button-container-color', palette.primary);
    style.setProperty('--mdc-protected-button-label-text-color', onPrimary);
    style.setProperty('--mat-text-button-label-text-color', palette.primary);
    style.setProperty('--mat-text-button-state-layer-color', palette.primary);
    style.setProperty('--mdc-outlined-button-label-text-color', palette.primary);
    style.setProperty('--mdc-outlined-button-outline-color', palette.primary);
    style.setProperty('--mat-icon-button-state-layer-color', palette.primary);
    style.setProperty('--mat-fab-container-color', palette.primary);
    style.setProperty('--mat-fab-foreground-color', onPrimary);
  }

  private contrastColor(color: string): string {
    let r: number, g: number, b: number;
    if (color.startsWith('#')) {
      const hex = color.length === 4
        ? color.slice(1).split('').map(c => c + c).join('')
        : color.slice(1, 7);
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      const m = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return '#ffffff';
      r = +m[1]; g = +m[2]; b = +m[3];
    }
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '#ffffff';
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#111111' : '#ffffff';
  }

  private getStoredAppColors(): AppColors | null {
    const rawAppColors = localStorage.getItem(this.storageKey);
    if (!rawAppColors) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawAppColors);
      return this.normalizeAppColors(parsed);
    } catch {
      return null;
    }
  }

  private extractAppColors(loginResponse: any): AppColors | null {
    const rawAppColors = loginResponse?.AppColors ?? loginResponse?.appColors ?? null;
    return this.parseAppColors(rawAppColors);
  }

  private normalizeAppColors(candidate: any): AppColors | null {
    const light = this.normalizePalette(candidate?.light);
    const dark = this.normalizePalette(candidate?.dark);

    if (!light || !dark) {
      return null;
    }

    return { light, dark };
  }

  private normalizePalette(candidate: any): AppColorPalette | null {
    const primary = this.normalizeColor(candidate?.primary);
    const secondary = this.normalizeColor(candidate?.secondary);
    const accent = this.normalizeColor(candidate?.accent);
    const background = this.normalizeColor(candidate?.background);
    const surface = this.normalizeColor(candidate?.surface);
    const text = this.normalizeColor(candidate?.text);

    if (!primary || !secondary || !accent || !background || !surface || !text) {
      return null;
    }

    return {
      primary,
      secondary,
      accent,
      background,
      surface,
      text,
    };
  }

  private getThemePalette(appColors: AppColors, theme: string | null): AppColorPalette | null {
    const selectedTheme = theme ?? localStorage.getItem('theme') ?? 'dark';
    return selectedTheme === 'light' ? appColors.light : appColors.dark;
  }

  private normalizeColor(color: unknown): string | null {
    if (typeof color !== 'string') {
      return null;
    }

    const value = color.trim();
    if (value.length === 0) {
      return null;
    }

    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) {
      return value;
    }

    if (/^rgb(a)?\(/i.test(value) || /^hsl(a)?\(/i.test(value)) {
      return value;
    }

    return null;
  }
}